import { isDevelopment } from '@/shared/utils/import-meta'

/** Target bitrate for screen share: enough for text/scroll without macroblocking. */
export const SCREEN_SHARE_MAX_BITRATE_BPS = 2_500_000

/** Keep FPS modest — resolution is preferred over framerate for screen content. */
export const SCREEN_SHARE_MAX_FRAMERATE = 15

export type ScreenShareEncodingParams = {
  maxBitrate: number
  maxFramerate: number
  degradationPreference: RTCDegradationPreference
}

export const getScreenShareEncodingParams = (): ScreenShareEncodingParams => ({
  maxBitrate: SCREEN_SHARE_MAX_BITRATE_BPS,
  maxFramerate: SCREEN_SHARE_MAX_FRAMERATE,
  degradationPreference: 'maintain-resolution',
})

export const applyScreenShareSenderParams = async (sender: RTCRtpSender): Promise<boolean> => {
  if (!sender?.track || sender.track.kind !== 'video') {
    return false
  }

  if (typeof sender.getParameters !== 'function' || typeof sender.setParameters !== 'function') {
    return false
  }

  try {
    const params = sender.getParameters()
    if (!params.encodings?.length) {
      params.encodings = [{}]
    }

    const encoding = getScreenShareEncodingParams()
    params.encodings = params.encodings.map((enc) => ({
      ...enc,
      maxBitrate: encoding.maxBitrate,
      maxFramerate: encoding.maxFramerate,
    }))
    params.degradationPreference = encoding.degradationPreference

    await sender.setParameters(params)

    if (isDevelopment()) {
      console.debug('[screen-share] Applied sender encoding', {
        trackId: sender.track.id,
        maxBitrate: encoding.maxBitrate,
        maxFramerate: encoding.maxFramerate,
        degradationPreference: encoding.degradationPreference,
      })
    }

    return true
  } catch (error) {
    console.warn('[screen-share] Failed to apply sender encoding params', error)
    return false
  }
}

export const findVideoSenderByTrackId = (
  peerConnection: RTCPeerConnection | undefined,
  trackId: string,
): RTCRtpSender | undefined => {
  return peerConnection?.getSenders().find((sender) => sender.track?.id === trackId)
}

/**
 * Applies screen-share encoding after replaceTrack / renegotiation.
 * Retries briefly — encodings may be empty until negotiation completes.
 */
export const applyScreenShareSenderParamsWithRetry = async (
  peerConnection: RTCPeerConnection | undefined,
  trackId: string,
  retries = 3,
  delayMs = 300,
): Promise<void> => {
  for (let attempt = 0; attempt < retries; attempt++) {
    const sender = findVideoSenderByTrackId(peerConnection, trackId)
    if (sender) {
      const applied = await applyScreenShareSenderParams(sender)
      const encodings = sender.getParameters?.()?.encodings
      if (applied && encodings?.length) {
        return
      }
    }

    if (attempt < retries - 1) {
      await new Promise((resolve) => setTimeout(resolve, delayMs))
    }
  }
}
